import { useEffect, useMemo, useRef, useState } from "react";
import {
	useMaterialReactTable,
	MaterialReactTable,
} from "material-react-table";
import { Box, Tooltip,IconButton } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { useNavigate } from "react-router-dom";
import "./Admin.scss";
import { ICONS, Button, Icon, Input, Label } from "abs-ui-components";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { addNewUser, postUsersInfo } from "../../../services/AdminService";
import AbsModal from "../../../common/absModal/AbsModal";
import ClearIcon from "@mui/icons-material/Clear";

const UserDetails = () => {
	const [usersInfoData, setUsersInfoData] = useState([]);
	const [userSearchValue, setUserSearchValue] = useState("");
	const [userPagination, setUserPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [rowCount, setRowCount] = useState(0);
	const [addUser, setAddUser] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [userSort, setUsersort] = useState([]);
	const [previousGlobalFilter, setPreviousGlobalFilter] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const navigate = useNavigate();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm();

	const previousSortOptions = useRef();
	const previousPagination = useRef();

	useEffect(() => {
		let sortOptions = userSort.map((x) => ({
			sortField: x.id,
			isAscending: !x.desc,
		}));

		if (previousGlobalFilter !== userSearchValue) {
			setPreviousGlobalFilter(userSearchValue);
			setUserPagination({ ...userPagination, pageIndex: 0 });
			getUsersDetails(
				userSearchValue,
				0,
				userPagination.pageSize,
				sortOptions
			);
		} else if (
			JSON.stringify(sortOptions) !==
				JSON.stringify(previousSortOptions.current) ||
			JSON.stringify(userPagination) !==
				JSON.stringify(previousPagination.current)
		) {
			previousSortOptions.current = sortOptions;
			previousPagination.current = userPagination;
			getUsersDetails(
				userSearchValue,
				userPagination.pageIndex,
				userPagination.pageSize,
				sortOptions
			);
		}
	}, [userSearchValue, userPagination, userSort]);

	const getUsersDetails = async (
		searchStr = "",
		pageIndex = 0,
		pageSize = 25,
		sortOptions
	) => {
		let requestPayload = {
			globalFilter: "",
			filters: [],
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
			sortOption: {
				sortField: "",
				isAscending: false,
			},
		};

		if (searchStr !== "") {
			requestPayload.globalFilter = searchStr;
		} else {
			requestPayload.globalFilter = "";
		}

		if (sortOptions?.length) {
			requestPayload.sortOption = sortOptions[0];
		}

		try {
			setLoading(true);
			const response = await postUsersInfo(requestPayload);
			if (response?.data) {
				setUsersInfoData(response?.data || []);
				setRowCount(response?.pagination?.totalItems || 0);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const userColumns = useMemo(
		() => [
			{
				header: "Name",
				enableHiding: false,
				accessorKey: "firstName",
				accessorFn: (row) => {
					if (row?.firstName || row?.lastName)
						return row?.firstName + " " + row?.lastName;
				},
			},
			{
				header: "Email",
				accessorKey: "email",
			},
			{
				header: "Role",
				accessorKey: "roles",
				accessorFn: (row) => row?.roles?.join(", "),
			},
			{
				header: "Organization",
				accessorKey: "organization",
			},
		],
		[]
	);

	const manageUsersInfo = (userInfo) => {
		const selectedUserDetails = userInfo.original;
		sessionStorage.setItem(
			"userDetails",
			JSON.stringify(selectedUserDetails)
		);
		navigate("/manage-user-details");
	};

	const userDetailsTable = useMaterialReactTable({
		columns: userColumns,
		data: usersInfoData,
		enableStickyHeader: true,
		manualPagination: true,
		enableRowActions: true,
		enableColumnActions: false,
		enableGlobalFilterModes: false,
		enableColumnFilters: false,
		manualFiltering: true,
		rowCount,
		onGlobalFilterChange: (value) => {
			setSearchInput(value || "");
		},
		onSortingChange: setUsersort,
		onPaginationChange: setUserPagination,
		enableFullScreenToggle: false,
		enableDensityToggle: false,
		positionActionsColumn: "last",
		state: {
			isLoading: isLoading,
			globalFilter: searchInput,
			pagination: userPagination,
			sorting: userSort,
		},
		initialState: {
			density: "compact",
			columnOrder: ["Name", "Email", "Role", "Organization"],
		},
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex", gap: "1rem", width: "120px" }}>
				<Tooltip title="Manage">
					<Button onClick={() => manageUsersInfo(row)}>Manage</Button>
				</Tooltip>
			</Box>
		),
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 376px)",
			},
		},
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
				"& .MuiSvgIcon-root": {
					color: "white",
				},
			},
		},
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiSearchTextFieldProps: {
			placeholder: "Type and Enter",
			value: searchInput,
			onChange: (e) => {
				const value = e.target.value;
				setSearchInput(value);
				setUserPagination((prev) => ({ ...prev, pageIndex: 0 }));
				if (!value.trim()) {

					setUserPagination((prev) => ({
						...prev,
						pageIndex: 0,
					}));

					setUserSearchValue("");
				}
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
					setUserPagination((prev) => ({ ...prev, pageIndex: 0 }));
					setUserSearchValue(searchInput);
				}
			},
			InputProps: {
				endAdornment: searchInput && (
					<IconButton
						size="small"
						onClick={() => {
							setSearchInput("");
							setUserSearchValue("");
							setUserPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						sx={{ visibility: searchInput ? "visible" : "hidden" }}
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				),
			},
		},
	});

	const toggleOpen = () => {
		reset();
		setAddUser(!addUser);
	};

	const handleCreateUser = async (formData) => {
		if (formData.Password === formData.confirmPassword) {
			const addNewUserObj = {
				firstName: formData?.firstName,
				lastName: formData?.lastName,
				officeLocation: formData?.officeLocation,
				email: formData.email,
				organization: formData.organization,
			};
			try {
				const response = await addNewUser(addNewUserObj);
				if (response && response.isSuccess) {
					getUsersDetails();
					emitToasterMsg("success", "User created successfully");
					toggleOpen();
				} else if (response && response?.isSuccess === false) {
					emitToasterMsg("error", response.errorMessage);
					toggleOpen();
				}
			} catch (error) {
				toggleOpen();
			}
		}
	};

	return (
		<>
			<div className="d-flex justify-content-between">
				<h4>Users ({rowCount})</h4>
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={toggleOpen}
				>
					{" "}
					Add User
				</Button>
			</div>
			<div className="mt-2">
				<MaterialReactTable table={userDetailsTable} />
			</div>
			<AbsModal
				isOpen={addUser}
				title="Add User"
				onClose={setAddUser}
				close={toggleOpen}
			>
				<form onSubmit={handleSubmit(handleCreateUser)}>
					<div className="form-container">
						<MDBRow className="mt-5">
							<MDBCol md="6">
								<div className="input-content mt-3">
									<Label name="First Name" bold required />
									<Controller
										name="firstName"
										control={control}
										rules={{
											required: "First Name is required",
										}}
										render={({ field }) => (
											<>
												<Input
													id="FirstName"
													type="text"
													onChange={field.onChange}
													value={field.value || ""}
													autoFocus={false}
													className="input-content mt-2"
												/>
												{errors["firstName"] && (
													<p className="error">
														{
															errors["firstName"]
																.message
														}
													</p>
												)}
											</>
										)}
									/>
								</div>
							</MDBCol>
							<MDBCol md="6">
								<div className="input-content mt-3">
									<Label name="Last Name" bold required />
									<Controller
										name="lastName"
										control={control}
										rules={{
											required: "Last Name is required",
										}}
										render={({ field }) => (
											<>
												<Input
													id="lastName"
													type="text"
													onChange={field.onChange}
													value={field.value || ""}
													autoFocus={false} // Adjust autoFocus as needed
													className="input-content mt-2"
												/>
												{errors["lastName"] && (
													<p className="error">
														{
															errors["lastName"]
																.message
														}
													</p>
												)}
											</>
										)}
									/>
								</div>
							</MDBCol>
						</MDBRow>

						<MDBRow className="mt-5">
							<MDBCol md="6">
								<div className="input-content mt-3">
									<Label name="Email" bold required />
									<Controller
										name="email"
										control={control}
										rules={{
											required: "Email is required",
											pattern: {
												value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
												message:
													"Invalid email address",
											},
										}}
										render={({ field }) => (
											<>
												<Input
													id="email"
													onChange={field.onChange}
													value={field.value || ""}
													autoFocus={false} // Adjust autoFocus as needed
													className="input-content mt-2"
												/>
												{errors["email"] && (
													<p className="error">
														{
															errors["email"]
																.message
														}
													</p>
												)}
											</>
										)}
									/>
								</div>
							</MDBCol>
							<MDBCol md="6">
								<div className="input-content mt-3">
									<Label name="Organization" bold required />
									<Controller
										name="organization"
										control={control}
										rules={{
											required:
												"Organization is required",
										}}
										render={({ field }) => (
											<>
												<Input
													id="organization"
													type="text"
													onChange={field.onChange}
													value={field.value || ""}
													autoFocus={false} // Adjust autoFocus as needed
													className="input-content mt-2"
												/>
												{errors["organization"] && (
													<p className="error">
														{
															errors[
																"organization"
															].message
														}
													</p>
												)}
											</>
										)}
									/>
								</div>
							</MDBCol>
						</MDBRow>

						<MDBRow className="mt-5">
							<MDBCol md="6">
								<div className="input-content mt-3">
									<Label
										name="Office Location"
										bold
										required
									/>
									<Controller
										name="officeLocation"
										control={control}
										rules={{
											required:
												"Office Location is required",
										}}
										render={({ field }) => (
											<>
												<Input
													id="officeLocation"
													type="text"
													onChange={field.onChange}
													value={field.value || ""}
													autoFocus={false} // Adjust autoFocus as needed
													className="input-content mt-2"
												/>
												{errors["officeLocation"] && (
													<p className="error">
														{
															errors[
																"officeLocation"
															].message
														}
													</p>
												)}
											</>
										)}
									/>
								</div>
							</MDBCol>
						</MDBRow>

						<div className="d-flex justify-content-end fixed-action-btns pt-5 ml-2">
							<button
								type="submit"
								className="Create text-center"
							>
								Create
							</button>
							<button
								onClick={toggleOpen}
								className="cancel-btn ml-2"
								type="button"
							>
								Cancel
							</button>
						</div>
					</div>
				</form>
			</AbsModal>
		</>
	);
};

export default UserDetails;
